import React, { useState, useEffect } from 'react';
import { createI18n } from '@/Utils/i18n';
import axios from '@/Data/Axios';

const translations = {
    'Help Request': { id: 'Permintaan Bantuan', ko: '도움 요청' },
    'We will help you with this document': { id: 'Kami akan membantu Anda dengan dokumen ini', ko: '이 서류에 대해 도와드리겠습니다' },
    'Full Name': { id: 'Nama Lengkap', ko: '성명' },
    'Email Address': { id: 'Alamat Email', ko: '이메일 주소' },
    'Phone Number': { id: 'Nomor Telepon', ko: '전화번호' },
    'Message': { id: 'Pesan', ko: '메시지' },
    'Send Request': { id: 'Kirim Permintaan', ko: '요청 보내기' },
    'Processing...': { id: 'Memproses...', ko: '처리 중...' },
    'Required field': { id: 'Bidang wajib', ko: '필수 입력' },
    'Request sent successfully': { id: 'Permintaan berhasil dikirim', ko: '요청이 성공적으로 전송되었습니다' },
    'Failed to send request': { id: 'Gagal mengirim permintaan', ko: '요청 전송에 실패했습니다' },
    'Cancel': { id: 'Batal', ko: '취소' }
};

const t = createI18n(translations);

interface DocumentHelpModalProps {
    isOpen: boolean;
    onClose: () => void;
    documentName: string;
    universityName: string;
    trackName: string;
    sessionData?: {
        name?: string;
        email?: string;
    };
}

const DocumentHelpModal: React.FC<DocumentHelpModalProps> = ({
    isOpen,
    onClose,
    documentName,
    universityName,
    trackName,
    sessionData
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    useEffect(() => {
        if (isOpen && sessionData) {
            setFormData(prev => ({
                ...prev,
                name: sessionData.name || '',
                email: sessionData.email || ''
            }));
        }
    }, [isOpen, sessionData]);

    const handleSubmit = async () => {
        if (!formData.name || !formData.email || !formData.phone) {
            alert(t('Required field'));
            return;
        }

        setIsSubmitting(true);
        try {
            const currentDate = new Date().toLocaleString();
            const htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>Document Help Request</title>
                </head>
                <body>
                    <h2>Document Help Request</h2>
                    <p><strong>University:</strong> ${universityName}</p>
                    <p><strong>Track:</strong> ${trackName}</p>
                    <p><strong>Document:</strong> ${documentName}</p>
                    <p><strong>Name:</strong> ${formData.name}</p>
                    <p><strong>Email:</strong> ${formData.email}</p>
                    <p><strong>Phone:</strong> ${formData.phone}</p>
                    <p><strong>Message:</strong> ${formData.message || 'None'}</p>
                    <p><strong>Time:</strong> ${currentDate}</p>
                </body>
                </html>
            `;

            const response = await axios.post('/admin/trigger-email', {
                email: 'dl_mate@webtoon.today',
                subject: `[📄Document Help] ${universityName} - ${documentName} - ${formData.name}`,
                body: htmlContent,
                groups: 'dl_mate@webtoon.today'
            });

            if (response.data && response.data.code === 200) {
                alert(t('Request sent successfully'));
                setFormData({ name: '', email: '', phone: '', message: '' });
                onClose();
            } else {
                throw new Error('Email sending failed');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            alert(t('Failed to send request'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        }}>
            <div className="modal-content" style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto'
            }}>
                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ margin: '0 0 8px 0' }}>{t('Help Request')}</h3>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                        {t('We will help you with this document')}: {documentName}
                    </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                            {t('Full Name')} *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '6px',
                                fontSize: '14px'
                            }}
                            placeholder={t('Full Name')}
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                            {t('Email Address')} *
                        </label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '6px',
                                fontSize: '14px'
                            }}
                            placeholder="your.email@example.com"
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                            {t('Phone Number')} *
                        </label>
                        <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '6px',
                                fontSize: '14px'
                            }}
                            placeholder="+62 812345678"
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                            {t('Message')}
                        </label>
                        <textarea
                            value={formData.message}
                            onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                            rows={4}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '6px',
                                fontSize: '14px',
                                resize: 'vertical'
                            }}
                            placeholder="Describe your issue with this document"
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            style={{
                                flex: 1,
                                padding: '12px',
                                backgroundColor: '#7b4397',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.6 : 1
                            }}
                        >
                            {isSubmitting ? t('Processing...') : t('Send Request')}
                        </button>
                        <button
                            onClick={onClose}
                            disabled={isSubmitting}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#f0f0f0',
                                color: '#333',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            {t('Cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentHelpModal;